import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { engineContext, isFormType, useInspectionForm, useSaveFormValues, type FormType, type InspectionForm } from '../../../api/forms';
import { useInspection, type Inspection } from '../../../api/inspections';
import { useEnumLabel } from '../../../api/enums';
import { isApiError } from '../../../api/errors';
import { Breadcrumbs, Button, ButtonGroup, ButtonLink, ErrorSummary, InsetText, RequiredFieldsNote, type ErrorSummaryItem } from '../../../components/idsk';
import { toast } from '../../../components/common/toasts';
import { VersionConflictSummary } from '../../../components/common/VersionConflict';
import { PageHeading } from '../../../layout/PageHeading';
import { usePageTitle } from '../../../layout/usePageTitle';
import { evaluate, MISSING_CODES, type EvaluationResult, type FormValues } from '../../../form-engine';
import { FormRenderer, errorSummaryItems, hasRequiredFields, type Lang } from '../../../form-engine/react/FormRenderer';
import { FormSummary } from '../../../form-engine/react/FormSummary';
import { NotFoundPage } from '../../common/NotFoundPage';
import { LoadingPage } from '../../common/LoadingPage';
import { QueryError } from '../../common/QueryError';
import { CompletenessTag } from './InspectionFormsTab';
import { FormDocuments } from '../documents/FormDocuments';

export function InspectionFormPage() {
  const { id, type } = useParams();
  const { t } = useTranslation();
  const label = useEnumLabel();
  const formType = isFormType(type) ? type : undefined;
  const inspection = useInspection(id);
  const form = useInspectionForm(id, formType);
  const title = formType ? label('formTypes', formType) : '';
  usePageTitle([title, inspection.data?.number, t('inspections.title')]);
  if (!formType) return <NotFoundPage />;
  if (inspection.error) return <QueryError error={inspection.error} asPage />;
  if (form.error) return <QueryError error={form.error} asPage />;
  if (inspection.isPending || form.isPending) return <LoadingPage />;
  return <FormPage key={`${id}-${formType}`} inspection={inspection.data} form={form.data} formType={formType} title={title} reload={() => form.refetch()} />;
}

function FormPage({ inspection, form, formType, title, reload }: { inspection: Inspection; form: InspectionForm; formType: FormType; title: string; reload: () => Promise<{ data?: InspectionForm }> }) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const label = useEnumLabel();
  const save = useSaveFormValues(inspection.id, formType);
  const [values, setValues] = useState<FormValues>(form.values);
  const [resetKey, setResetKey] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [summary, setSummary] = useState<ErrorSummaryItem[]>([]);
  const [conflict, setConflict] = useState(false);
  const [count, setCount] = useState(0);
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const context = useMemo(() => engineContext(form), [form]);

  const reset = (next: InspectionForm) => {
    setValues(next.values);
    setResetKey((k) => k + 1);
  };

  const onSave = async () => {
    setConflict(false);
    const current = evaluate(form.definition, values, context);
    const invalid = current.errors.filter((e) => !MISSING_CODES.has(e.code));
    if (invalid.length) {
      // invalid data is never saved (missing data is fine in a draft)
      setShowErrors(true);
      setSummary(errorSummaryItems(formType, form.definition, invalid, t, lang));
      setCount((c) => c + 1);
      return;
    }
    try {
      const saved = await save.mutateAsync({ data: values, version: form.version! });
      reset(saved);
      setSummary([]);
      setShowErrors(false);
      toast.success(saved.complete ? t('forms.savedComplete') : t('forms.savedIncomplete', { count: saved.errors.length }));
    } catch (e) {
      setCount((c) => c + 1);
      if (isApiError(e) && e.isVersionConflict) return setConflict(true);
      setShowErrors(true);
      setSummary(isApiError(e) && e.fieldErrors.length ? e.fieldErrors.map((fe) => ({ text: fe.message })) : [{ text: isApiError(e) ? e.message : t('errors.unexpected') }]);
    }
  };

  const showMissing = () => {
    setShowErrors(true);
    setSummary(errorSummaryItems(formType, form.definition, result?.errors ?? [], t, lang));
    setCount((c) => c + 1);
  };

  const base = `/inspekcie/${inspection.id}`;
  return (
    <>
      <Breadcrumbs items={[{ text: t('nav.home'), to: '/' }, { text: t('inspections.title'), to: '/inspekcie' }, { text: inspection.number, to: base }, { text: title }]} />
      {conflict && (
        <VersionConflictSummary
          focusKey={count}
          onReload={() =>
            void reload().then((r) => {
              if (r.data) reset(r.data);
              setConflict(false);
            })
          }
        />
      )}
      {summary.length > 0 && <ErrorSummary errors={summary} focusKey={count} />}
      <PageHeading caption={`${inspection.number} – ${inspection.name}`}>{title}</PageHeading>
      <p className="govuk-body">
        {t('forms.phaseLabel')}: {t(`forms.phases.${form.phase}`)} · <CompletenessTag form={{ exists: form.exists, complete: result?.complete ?? form.complete }} /> ·{' '}
        {t('templates.versionN', { n: form.templateVersionNo })}
      </p>
      {form.editable ? (
        <>
          <InsetText>
            {result?.complete ?? form.complete ? t('forms.complete') : t('forms.incompleteCount', { count: result?.errors.length ?? form.errors.length })}{' '}
            {!(result?.complete ?? form.complete) && (
              <Button variant="secondary" className="govuk-!-margin-bottom-0" onClick={showMissing}>
                {t('forms.showMissing')}
              </Button>
            )}
          </InsetText>
          <p className="govuk-body">
            <ButtonLink to={`${base}/formulare/${formType}/polia`} variant="secondary">
              {t('forms.editFields')}
            </ButtonLink>
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onSave();
            }}
            noValidate
          >
            {hasRequiredFields(form.definition) && <RequiredFieldsNote />}
            <FormRenderer formType={formType} definition={form.definition} values={values} onChange={setValues} context={context} lang={lang} showErrors={showErrors} resetKey={resetKey} onEvaluate={setResult} />
            <ButtonGroup>
              <Button type="submit" preventDoubleClick>
                {t('forms.save')}
              </Button>
              <ButtonLink to={`${base}`} variant="secondary">
                {t('forms.backToInspection')}
              </ButtonLink>
            </ButtonGroup>
          </form>
        </>
      ) : (
        <>
          <InsetText>
            {form.phase === 'FUTURE'
              ? t('forms.futureInfo', { status: label('inspectionStatuses', formType) })
              : form.phase === 'PAST' || inspection.status === 'FINISHED'
                ? t('forms.pastInfo')
                : t('forms.readOnlyInfo')}
          </InsetText>
          <FormSummary definition={form.definition} values={form.values} context={context} lang={lang} />
        </>
      )}
      <FormDocuments inspectionId={inspection.id} formType={formType} title={title} />
    </>
  );
}
